import { useMemo, useState } from 'react';
import { useApp } from '../store';
import { epicBackground } from '../epics';
import { epicsInUse, epicCardCount } from '../epic-ops';
import { RenameEpicDialog, DeleteEpicDialog } from './EpicManageDialogs';

/*
 * THE BOARD'S EPICS — the one classification you could set but never tidy. It sits
 * in Settings › Workflow beside Phases, Areas, Types and Labels, so every way of
 * classifying work is managed in one place.
 *
 * UNLIKE its neighbours, an epic is FREE TEXT, not a stored object (see EpicField) —
 * the name exists only through the cards carrying it. So, on purpose, this panel has:
 *   - NO "Add" button — you add an epic by typing it on a card's Epic field.
 *   - NO colour to pick — an epic's tint is hashed from its name.
 *   - NO "Retired" list — there is no object to retire; removing an epic has to
 *     change its cards (move them to another epic, or clear it off them), which the
 *     Delete dialog makes an explicit choice.
 * What it DOES give is the two things that were missing: rename (which merges when
 * the new name already exists) and delete. Both act on this board's cards.
 */
export default function EpicSettings() {
  const issues = useApp((s) => s.issues);
  const [dialog, setDialog] = useState<{ kind: 'rename' | 'delete'; name: string } | null>(null);

  const epics = useMemo(() => epicsInUse(issues), [issues]);

  return (
    <section className="panel" data-reveal="epics">
      <h2>Epics</h2>
      <p className="hint">
        A large body of work a card belongs to — “Microsoft 365 rollout”, “Network refresh” —
        spanning phases and areas. Epics are added on cards: type one on a card’s Epic field.
        Here you rename or remove the epics this board is using.
      </p>

      {epics.length === 0 ? (
        <p className="hint">
          No epics in use yet — type one on a card’s Epic field to start grouping work under an epic.
        </p>
      ) : (
        <ul className="area-list">
          {epics.map((name) => {
            const n = epicCardCount(issues, name);
            return (
              <li key={name} className="area-row">
                <div className="area-line">
                  <span className="area-dot" style={{ background: epicBackground(name, epics) || 'var(--t3)' }} />
                  <span className="area-name-flat">{name}</span>
                  <span className="area-used">{n} {n === 1 ? 'card' : 'cards'}</span>
                  <button className="btn btn-small" onClick={() => setDialog({ kind: 'rename', name })}>
                    Change
                  </button>
                  <button className="btn btn-small" onClick={() => setDialog({ kind: 'delete', name })}>
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="hint area-note">
        <b>An epic is only a name on its cards.</b> Removing one asks what happens to those cards —
        move them to another epic, or clear it off them. Unlike an area or a label there is nothing
        to retire, so there is no Retired list.
      </p>

      {dialog?.kind === 'rename' && (
        <RenameEpicDialog name={dialog.name} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'delete' && (
        <DeleteEpicDialog name={dialog.name} onClose={() => setDialog(null)} />
      )}
    </section>
  );
}
